import SimpleBar from "simplebar-react"
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Translate } from "../../../components/translate/Translate";
import { useTranslation } from "react-i18next";
import "./styles/ProjectsSelect.scss"
import useNoun from "../../../hooks/useNoun";
import { IProject } from "../types";

interface IProjectsSelect {
	displayAll?: boolean
	chosenItems: IProject[]
	setChosenItems: (list: IProject[]) => void
	list: IProject[]
}

export default function ProjectsSelect({ displayAll, chosenItems, setChosenItems, list } : IProjectsSelect) {

	// Ensure list is always an array
	const safeList = useMemo(() => Array.isArray(list) ? list : [], [list]);

	const [searchValue, setSearchValue] = useState(""),
	[isActive, setIsActive] = useState(false),
	mainRef = useRef<HTMLDivElement>(null),
	allCheckboxRef = useRef<HTMLInputElement>(null),
	getNoun = useNoun(),
	{ t } = useTranslation();

	const handleSelectProject = (project: IProject) => {
		if (!chosenItems) return;
		if (chosenItems.some(item => item.id_num === project.id_num)) {
			setChosenItems(chosenItems.filter(item => item.id_num !== project.id_num))
		} else {
			const result: IProject[] = [];
			safeList.forEach(item => {
				if(item.id_num === project.id_num) {
					result.push(project);
				}
				if(chosenItems.some(chosenItem => chosenItem.id_num === item.id_num)) {
					result.push(item);
				}
			})
			
			setChosenItems(result);
		}
	}

	const handleCheckedAll = useCallback((checkAll: boolean) => {
		const chosenLength = chosenItems?.length || 0;
		setChosenItems((safeList.length !== chosenLength && checkAll) ? safeList : []);
	}, [chosenItems, safeList, setChosenItems]);

	useEffect(() => {
		if(mainRef.current) {
			const handleClickOutside = (event: MouseEvent) => {
				if (mainRef.current && !mainRef.current.contains(event.target as Node)) {
					setIsActive(false);
				}
			};
			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [mainRef]);

	useEffect(() => {
		if(displayAll && allCheckboxRef.current && chosenItems && chosenItems.length === 0) {
			setChosenItems(safeList);
			allCheckboxRef.current.checked = true;
		}
		if(chosenItems && chosenItems.length === safeList.length && allCheckboxRef.current) {
			allCheckboxRef.current.checked = true;
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if(!allCheckboxRef.current) return;
		if(!chosenItems || chosenItems.length !== safeList.length) {
			allCheckboxRef.current.checked = false;
		} else {
			allCheckboxRef.current.checked = true;
		}
	}, [chosenItems, safeList]);

	const label = 
		(chosenItems && chosenItems.length) 
	? 
		chosenItems.length === safeList.length 
	? 
		t("analytics_page.statistic_report.all_projects")
	:
		`${getNoun(chosenItems.length, "projects")}`
	:
		t("analytics_page.statistic_report.project_select");

	return (
		<div className={`projects-select drop-down${isActive ? " is-active" : ""}${displayAll ? " on-left" : ""}`} ref={mainRef}>
			<button className="projects-select__target drop-down__target" type="button" aria-label={(chosenItems && chosenItems.length) ? `${getNoun(chosenItems.length, "projects")}` : t("analytics_page.statistic_report.project_select")} onClick={() => setIsActive(!isActive)}>
				<span>
					<span>
						{label}
					</span>
				</span>
				<svg width="24" height="24">
					<use href="#drop-down-arrow"></use>
				</svg>
			</button>
			<div className="projects-select__block drop-down__block">
				<SimpleBar className="projects-select__block-inner">
					<ul className="projects-select__list">
						{(safeList && safeList.length) ? (
							<li key="all-projects">
								<label className="popup-checkbox">
									<input onChange={event => handleCheckedAll(event.currentTarget.checked)}
										type="checkbox" name="all"
										className="popup-checkbox__input"
										ref={allCheckboxRef}
									/>
									<span className="popup-checkbox__element">
										<svg width="11" height="8" viewBox="0 0 11 8">
											<use xlinkHref="#check"></use>
										</svg>
									</span>
									<span className="popup-checkbox__text" style={{
										whiteSpace: "nowrap",
										textOverflow: "ellipsis",
										overflow: "hidden"
									}}>
										<b>{t("analytics_page.statistic_report.all_projects")}</b>
									</span>
								</label>
							</li>
						) : null}
						{
							(safeList && safeList.length) ? safeList
								.filter(project => chosenItems?.some(item => item.id_num === project.id_num))
								.filter(item => `${item.name}_${item.description}`.toLowerCase().includes(searchValue.toLowerCase()))
								?.map(project =>
								<li key={`${project.name}-${project.id_num}`}>
									<label className="popup-checkbox">
										<input onChange={_ => handleSelectProject(project)}
											checked={chosenItems?.some(item => item.id_num === project.id_num)}
											type="checkbox" name={project.name}
											className="popup-checkbox__input"
										/>
										<span className="popup-checkbox__element">
											<svg width="11" height="8" viewBox="0 0 11 8">
												<use xlinkHref="#check"></use>
											</svg>
										</span>
										<span className="popup-checkbox__text" style={{
											whiteSpace: "nowrap",
											textOverflow: "ellipsis",
											overflow: "hidden"
										}}>
											{project.name}_{project.description}
										</span>
									</label>
								</li>
							) : <></>
						}
						{
							(safeList && safeList.length) ? safeList
								.filter(project => !(chosenItems?.some(item => item.id_num === project.id_num)))
								.filter(item => `${item.name}_${item.description}`.toLowerCase().includes(searchValue.toLowerCase()))
								?.map(project =>
								<li key={`${project.name}-${project.id_num}`}>
									<label className="popup-checkbox">
										<input onChange={_ => handleSelectProject(project)}
											checked={chosenItems?.some(item => item.id_num === project.id_num)}
											type="checkbox" name={project.name}
											className="popup-checkbox__input"
										/>
										<span className="popup-checkbox__element">
											<svg width="11" height="8" viewBox="0 0 11 8">
												<use xlinkHref="#check"></use>
											</svg>
										</span>
										<span className="popup-checkbox__text" style={{
											whiteSpace: "nowrap",
											textOverflow: "ellipsis",
											overflow: "hidden"
										}}>
											{project.name}_{project.description}
										</span>
									</label>
								</li>
							) : <></>
						}
					</ul>
				</SimpleBar>
				<div className="projects-select__search">
					<label>
						<input type="search" name="search" required
							onChange={e => setSearchValue(e.target.value)} value={searchValue}
							className="input"
							placeholder={`${t("employees_admin.others.search_a_project")}`}
						/>
					</label>
					<button className="btn is-grey" type="submit">
						<Translate>employees_page.table.search</Translate>
						<svg width="15" height="15" viewBox="0 0 15 15">
							<use xlinkHref="#search"></use>
						</svg>
					</button>
				</div>
			</div>
		</div>
	)
}